export type AccessoryOrderStatus = 'paid' | 'fulfilling' | 'shipped' | 'delivered' | 'refunded' | 'cancelled';

export const ACCESSORY_ORDER_STATUSES: AccessoryOrderStatus[] = ['paid', 'fulfilling', 'shipped', 'delivered', 'refunded', 'cancelled'];

export interface AccessoryLineItem {
    productId?: string;
    slug?: string;
    stripePriceId?: string;
    stripeProductId?: string;
    name: string;
    quantity: number;
    unitAmount: number;
    amountSubtotal: number;
    currency: string;
    image?: string;
}

/**
 * What bought this order. `accessories` = a normal /accessories cart checkout;
 * `course33` = the Harmony Board that ships with a Course 33 purchase (its money
 * belongs to the course, so the single line item carries the session subtotal,
 * not a separate board price); `course33_comp` = the same board for a member an
 * admin added by hand; `bundle` = the Board + Band + Ring that ship with a
 * /bundle subscription.
 *
 * ABSENT means `accessories` — every order predating the field was a cart
 * checkout, so never filter undefined out.
 *
 * MUST stay in step with the API's `AccessoryOrderSource`: a value missing here
 * renders as the raw string in the Source column, which is how `course33_comp`
 * showed up verbatim in the CRM.
 */
export type AccessoryOrderSource = 'accessories' | 'course33' | 'course33_comp' | 'bundle';

export const ORDER_SOURCE_LABELS: Record<AccessoryOrderSource, string> = {
    accessories: 'Shop',
    course33: 'Course 33',
    // A comped or off-platform Course 33 member — no Stripe session at all, so
    // the order arrives with no address and a zero total until an admin records
    // what was actually collected.
    course33_comp: 'Course 33 (comped)',
    bundle: 'Bundle',
};

/** Which channels a shipment update may reach this customer through. */
export interface OrderNotificationChannels {
    email?: boolean;
    telegram?: boolean;
    inApp?: boolean;
}

export interface ShippingAddress {
    line1: string;
    line2?: string;
    city: string;
    state?: string;
    postal_code: string;
    country: string;
}

/** An accessory order (mirrors the API `AccessoryOrder`). Amounts are in the smallest currency unit. */
/**
 * Normalised carrier states. The API maps every provider's vocabulary into these
 * five, so nothing here depends on which provider answered.
 */
export type TrackingStatus = 'pending' | 'in_transit' | 'out_for_delivery' | 'delivered' | 'exception';

/** One carrier scan. */
export interface TrackingEvent {
    at: string;
    status: TrackingStatus;
    description: string;
    location?: string;
}

export default interface Order {
    id: string;
    orderNumber: string;
    userId?: string;
    email: string;
    status: AccessoryOrderStatus;
    lineItems: AccessoryLineItem[];
    subtotalAmount: number;
    shippingAmount: number;
    taxAmount: number;
    totalAmount: number;
    currency: string;
    shippingAddress?: ShippingAddress;
    shippingName?: string;
    phone?: string;
    stripeCheckoutSessionId: string;
    stripePaymentIntentId?: string;
    stripeCustomerId?: string;
    trackingNumber?: string;
    carrier?: string;
    trackingUrl?: string;
    /** Carrier scan history, newest first. Empty until auto-tracking is configured. */
    trackingEvents?: TrackingEvent[];
    /** The carrier's own wording for the current state, e.g. `In transit`. */
    trackingStatusDetail?: string;
    /** ISO timestamp the carrier was last polled. Absent = never auto-tracked. */
    trackingLastCheckedAt?: string;
    /** Why the last poll failed. STAFF-ONLY — never shown to the customer. */
    trackingLastError?: string;
    shippedAt?: string;
    deliveredAt?: string;
    cancelledAt?: string;
    refundId?: string;
    refundedAt?: string;
    adminNote?: string;
    /** Absent === `accessories` (rows predating the field). */
    source?: AccessoryOrderSource;
    /**
     * Internal triage flag — the course CRM's "Needs attention" bucket.
     *
     * NOT a seventh `status`: the buyer's order tracker and the Flutter app both
     * read the status enum, and this is staff-only. Always paired with
     * `adminNote` — the API rejects raising it without one.
     */
    needsAttention?: boolean;
    needsAttentionAt?: string;
    /** Per-order customer-notification switches. ABSENT means every channel is on. */
    notifications?: OrderNotificationChannels;
    /** Money collected outside Stripe, recorded by an admin. */
    manualPayment?: {
        amount: number;
        currency: string;
        reference?: string;
        collectedAt?: string;
    };
    created_at: string;
    updated_at: string;
}
